import { useState, useRef, useEffect, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { RefreshCcw } from "lucide-react";

const FaceDetectionCamera = ({
  onFaceDetectionChange,
  onPermissionStatusChange,
}) => {
  const [faceDetected, setFaceDetected] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [cameraActive, setCameraActive] = useState(false);
  const [retryingCamera, setRetryingCamera] = useState(false);

  const [position, setPosition] = useState({
  x: window.innerWidth - 240, // 240 = 192px (w-48) + margin
  y: window.innerHeight - 160, // 160 = 144px (h-36) + margin
});


  const { toast } = useToast();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const cameraRef = useRef(null);
  const faceDetectionRef = useRef(null);
  const mountedRef = useRef(true);
  const lastDetectionTimeRef = useRef(0);
  const faceBoundingBoxRef = useRef(null);

  // Load MediaPipe scripts dynamically
  const loadMediaPipeScripts = useCallback(async () => {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.FaceDetection && window.Camera) {
        resolve({ FaceDetection: window.FaceDetection, Camera: window.Camera });
        return;
      }

      const loadScript = (src) => {
        return new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = src;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      };

      // Load MediaPipe scripts in sequence
      Promise.all([
        loadScript(
          "https://cdn.jsdelivr.net/npm/@mediapipe/face_detection@0.4.1646425229/face_detection.js"
        ),
        loadScript(
          "https://cdn.jsdelivr.net/npm/@mediapipe/camera_utils@0.3.1640029074/camera_utils.js"
        ),
      ])
        .then(() => {
          // Wait a bit for the libraries to initialize
          setTimeout(() => {
            if (window.FaceDetection && window.Camera) {
              resolve({
                FaceDetection: window.FaceDetection,
                Camera: window.Camera,
              });
            } else {
              reject(new Error("MediaPipe libraries failed to load"));
            }
          }, 100);
        })
        .catch(reject);
    });
  }, []);

  // Face detection callback
  const onFacesDetected = useCallback(
    (results) => {
      if (!mountedRef.current) return;

      const faces = results.detections || [];
      const hasFace = faces.length > 0;

      // Update bounding box for visualization
      if (hasFace) {
        const face = faces[0];
        const boundingBox = face.boundingBox;
        faceBoundingBoxRef.current = {
          xCenter: boundingBox.xCenter,
          yCenter: boundingBox.yCenter,
          width: boundingBox.width,
          height: boundingBox.height,
        };
      } else {
        faceBoundingBoxRef.current = null;
      }

      // Only update state if needed to avoid re-renders
      if (faceDetected !== hasFace) {
        setFaceDetected(hasFace);
        onFaceDetectionChange(hasFace);
      }

      lastDetectionTimeRef.current = Date.now();
    },
    [faceDetected, onFaceDetectionChange]
  );

  // Cleanup resources
  const cleanupResources = useCallback(() => {

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (cameraRef.current) {
      try {
        cameraRef.current.stop();
      } catch (e) {
        console.error("Error stopping camera:", e);
      }
      cameraRef.current = null;
    }

    if (faceDetectionRef.current) {
      try {
        faceDetectionRef.current.close();
      } catch (e) {
        console.error("Error closing face detection:", e);
      }
      faceDetectionRef.current = null;
    }

    setCameraActive(false);
    faceBoundingBoxRef.current = null;
  }, []);

  // Initialize face detection
  const initializeFaceDetection = useCallback(async () => {
    if (!mountedRef.current) return false;

    try {
      const { FaceDetection, Camera } = await loadMediaPipeScripts();

      // Create new face detection instance
      const faceDetection = new FaceDetection({
        locateFile: (file) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/face_detection@0.4.1646425229/${file}`,
      });

      // Configure face detection
      faceDetection.setOptions({
        model: "short",
        minDetectionConfidence: 0.5,
      });

      // Set up detection callback
      faceDetection.onResults(onFacesDetected);

      // Wait for model to load
      await faceDetection.initialize();
      faceDetectionRef.current = faceDetection;

      return true;
    } catch (error) {
      console.error("Error initializing face detection:", error);
      return false;
    }
  }, [onFacesDetected, loadMediaPipeScripts]);

  // Request camera access
  const requestCameraPermission = useCallback(async () => {
    if (!mountedRef.current) return null;

    setInitializing(true);

    try {
      // Initialize face detection first
      const faceDetectionReady = await initializeFaceDetection();
      if (!faceDetectionReady) {
        throw new Error("Failed to initialize face detection");
      }

      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
      });

      if (!mountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return null;
      }

      streamRef.current = stream;

      // Connect stream to video element
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch((err) => {
          console.error("Error playing video:", err);
        });
      }

      // Create and start camera
      if (videoRef.current && faceDetectionRef.current && window.Camera) {
        const camera = new window.Camera(videoRef.current, {
          onFrame: async () => {
            try {
              if (
                faceDetectionRef.current &&
                videoRef.current?.readyState === 4
              ) {
                await faceDetectionRef.current.send({
                  image: videoRef.current,
                });
              }
            } catch (err) {
              console.error("Error in camera frame processing:", err);
            }
          },
          width: 640,
          height: 480,
        });

        await camera.start();
        cameraRef.current = camera;
      }

      onPermissionStatusChange(true);
      setCameraActive(true);

      // Wait for camera to stabilize
      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (!mountedRef.current) {
        cleanupResources();
        return null;
      }

      setInitializing(false);

      // Start periodic checks for detection timeouts
      startDetectionTimeoutCheck();

      return stream;
    } catch (error) {
      console.error("Camera permission error:", error);
      onPermissionStatusChange(false);
      setInitializing(false);
      throw error;
    }
  }, [
    cleanupResources,
    initializeFaceDetection,
    onFacesDetected,
    onPermissionStatusChange,
  ]);

  // Check for detection timeout
  const startDetectionTimeoutCheck = useCallback(() => {
    const intervalId = setInterval(() => {
      if (!mountedRef.current) {
        clearInterval(intervalId);
        return;
      }

      const now = Date.now();
      const timeSinceLastDetection = now - lastDetectionTimeRef.current;

      // If no detection events for 5 seconds, assume something is wrong
      if (
        lastDetectionTimeRef.current > 0 &&
        timeSinceLastDetection > 5000 &&
        cameraActive
      ) {
        console.warn(
          "Detection timeout - no detection events received recently"
        );
        setFaceDetected(false);
        onFaceDetectionChange(false);
      }
    }, 2000);

    return () => clearInterval(intervalId);
  }, [cameraActive, onFaceDetectionChange]);

  // Handle retry button click
  const retryFaceDetection = useCallback(async () => {
    if (retryingCamera || !mountedRef.current) return;

    setRetryingCamera(true);
    try {
      cleanupResources();

      setFaceDetected(false);
      onFaceDetectionChange(false);

      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (!mountedRef.current) {
        setRetryingCamera(false);
        return;
      }

      await requestCameraPermission();

      if (!mountedRef.current) {
        setRetryingCamera(false);
        return;
      }

      toast({
        title: "Camera Restarted",
        description: "Face detection system has been restarted",
      });
    } catch (error) {
      console.error("Error retrying face detection:", error);
      if (mountedRef.current) {
        toast({
          title: "Camera Error",
          description: "Failed to restart camera. Please check permissions.",
          variant: "destructive",
        });
      }
    } finally {
      if (mountedRef.current) {
        setRetryingCamera(false);
      }
    }
  }, [
    cleanupResources,
    requestCameraPermission,
    toast,
    retryingCamera,
    onFaceDetectionChange,
  ]);

  // Initialize on component mount
  useEffect(() => {
    mountedRef.current = true;
    requestCameraPermission();

    return () => {
      mountedRef.current = false;
      cleanupResources();
    };
  }, [requestCameraPermission, cleanupResources]);

  return (
    <div
  className="fixed w-48 h-36 bg-black rounded-md overflow-hidden shadow-lg z-50 border-2 border-primary"
  style={{
    bottom: `${position.y}px`,
    right: `${position.x}px`,
    cursor: "move",
  }}
  draggable
  onDragStart={(e) => {
    e.dataTransfer.setDragImage(new Image(), 0, 0); // Prevent ghost image
  }}
  onDrag={(e) => {
    if (e.clientX !== 0 && e.clientY !== 0) {
      setPosition({
        x: e.clientX - 96, // 96 = half of 192px (w-48)
        y: e.clientY - 72,  // 72 = half of 144px (h-36)
      });
    }
  }}
>
      {/* Video element for camera feed */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="w-full h-full object-cover"
      />
      <div className="absolute top-0 left-0 right-0 flex justify-between items-center p-2 bg-black bg-opacity-50">
        <Badge
          variant={faceDetected ? "success" : "destructive"}
          className="text-xs"
        >
          {initializing
            ? "Initializing..."
            : faceDetected
            ? "Face Detected"
            : "No Face Detected"}
        </Badge>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-white hover:text-primary hover:bg-black hover:bg-opacity-20"
          onClick={retryFaceDetection}
          disabled={retryingCamera || initializing}
        >
          <RefreshCcw
            className={`h-3 w-3 ${retryingCamera ? "animate-spin" : ""}`}
          />
        </Button>
      </div>

      {/* Draw bounding box if face is detected */}
      {faceDetected && faceBoundingBoxRef.current && (
        <div
          style={{
            position: "absolute",
            top: `${
              faceBoundingBoxRef.current.yCenter * 100 -
              faceBoundingBoxRef.current.height * 50
            }%`,
            left: `${
              faceBoundingBoxRef.current.xCenter * 100 -
              faceBoundingBoxRef.current.width * 50
            }%`,
            width: `${faceBoundingBoxRef.current.width * 100}%`,
            height: `${faceBoundingBoxRef.current.height * 100}%`,
            border: "2px solid green",
            borderRadius: "4px",
          }}
        />
      )}
    </div>
  );
};

export default FaceDetectionCamera;
